import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, defer, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import {
  Coverage,
  IMemberSearchResponse,
  MemberDetails,
  MemberSearchRequest,
  EligibilityMenu,
  Clients,
  RxTypeInterface,
  GroupLevelAccessResponse,
  UserAccessLevel,
  IClientPharmacyConfig
} from '@interfaces';
import { AppConstants } from 'src/app/app.constants';
import { AppCenterAnalytics, AppInsightsService, UtilService, SharedService, } from '@services';
import { EligibilityService, BenefitManagementService, DemoClientService, AuthService, CMSService } from '@core';
import {
  EligibilityMenus
} from '@shared';
import { LoaderService } from '@management';
import {
  clearSsnFormat,
  formatPhone,
  formatSSN,
  getQueryParam,
  searchClientId,
  trimIfUNE,
} from 'src/app/util';
@Component({
  selector: 'app-member-search',
  templateUrl: './member-search.component.html',
  styleUrls: ['./member-search.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class MemberSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginator') paginator: MatPaginator;
  public displayedColumns: string[] = [...AppConstants.DISPLAYED_MEMBER_SEARCH_COLUMNS];
  public coverageTableColumns: string[] = [
    'group',
    'client',
    'effectiveDate',
    'endDate',
    'status'
  ];
  public coverageTypesDisplayedColumns: string[] = [
    'memberRxType',
    'effectiveDate',
    'endDate',
  ];

  public memberMenus: EligibilityMenu[] = [];
  public checkTouchForForm = false;
  public searchClicked = false;
  public dataSource = new MatTableDataSource<MemberDetails>(
    [] as MemberDetails[]
  );
  public members$!: Observable<IMemberSearchResponse>;
  public listOfClients$: Observable<Clients[]>;
  public displayGender = AppConstants.DISPLAY_GENDER;
  public clientList: Clients[] = [];
  public clientIdsWithoutDigitalIdCard: string;
  public clientIdsWithoutMailOrders: string;
  public relationShip = AppConstants.RELATIONSHIP_VALUES;
  public displayCoverageStatus = AppConstants.DISPLAY_COVERAGE_STATUS;
  public totalLength: string | number = 0;
  public currentPageIndex = 0;

  allFieldsEmpty = true;

  cardHolderRC: string = AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER;
  public coverageTypes = AppConstants.COVERAGE_TYPES;

  public idTypes: string[] = [
    'Card/Alt ID',
    'SSN',
    'Client Internal Member ID',
    // 'Patient ID',
  ];

  public searchMemberFormGroup: FormGroup;
  public hasAccessToAddMember: boolean;
  public allRowsExpanded = false;
  public expandedElement: MemberDetails | null;
  dataLoaded = false;
  private subscription: Subscription[] = [];
  public groupLevelAccess: GroupLevelAccessResponse[] = [];
  public showAddMember = false;
  public isGroupLevelAccess: boolean;
  public filteredClients!: Observable<Clients[]>;
  public clientFilterControl = new FormControl();
  public hidePaginator = true;
  public clientPharmacySerchConfig: IClientPharmacyConfig[] = [];

  constructor(
    private readonly router: Router,
    private readonly utilService: UtilService,
    private readonly formBuilder: FormBuilder,
    public eligibilityService: EligibilityService,
    private benefitService: BenefitManagementService,
    private readonly appInsightService: AppInsightsService,
    private readonly loader: LoaderService,
    private demoClientService: DemoClientService,
    private sharedService: SharedService,
    private readonly authService: AuthService,
    private readonly cmsService: CMSService
  ) { }

  ngOnInit(): void {
    this.subscription.push(this.cmsService.getWordpressClientConfig().subscribe((res) =>
      this.clientIdsWithoutDigitalIdCard = res.DISABLE_DIGITAL_ID_CARDS
    ));
    this.eligibilityService.getClientPhramaciesConfig().pipe(take(1)).subscribe((res) => {
      this.clientPharmacySerchConfig = res;
    })
    this.subscription.push(this.cmsService.getWordpressClientConfig().subscribe((res) => this.clientIdsWithoutMailOrders = res.SUPPRESS_MAIL_ORDER_FOR_CLIENTS));
    this.appInsightService.trackPageView(AppCenterAnalytics.ONLINE_ELIGIBILITY);
    this.searchMemberFormGroup = this.formBuilder.group({
      client: ['', [Validators.required]],
      idType: ['', [Validators.required]],
      idValue: [
        '',
        [
          Validators.required,
          Validators.maxLength(40),
          Validators.pattern('^[a-zA-Z0-9-]*$'),
        ],
      ],
      lastName: ['', [Validators.maxLength(35)]],
      firstName: ['', [Validators.maxLength(35)]],
    });

    this.searchMemberFormGroup.patchValue(
      this.eligibilityService.searchCriteriaCache
    );
    this.totalLength = this.eligibilityService.searchCriteriaCache?.total_count;
    if(this.eligibilityService.searchCriteriaCache?.idValue || this.eligibilityService.searchCriteriaCache?.firstName || this.eligibilityService.searchCriteriaCache?.lastName) {
      this.searchClicked = true;
    }

    if (this.searchMemberFormGroup.value.idType === 'SSN') {
      this.searchMemberFormGroup.controls.idValue.setValue(this.formatSSNString(this.searchMemberFormGroup.value.idValue));
    }

    this.eligibilityService.isGroupLevelAccess$.subscribe((r) => {
      this.isGroupLevelAccess = r;
      if (this.isGroupLevelAccess) {
        this.sharedService.getUserGroupsAccess({ emailId: this.authService.userEmail, clientId: this.searchMemberFormGroup.value.client }).pipe(tap((res) => {
          this.groupLevelAccess = res;
        })).subscribe();
      }
    });

    this.searchMemberFormGroup.valueChanges.subscribe((formValue) => {
      const formControls = Object.keys(formValue);
      const areAllFieldsEmpty = formControls.every((controlName) => {
        const control = this.searchMemberFormGroup.get(controlName);
        return (
          control.value === null ||
          control.value === undefined ||
          control.value === ''
        );
      });

      if (areAllFieldsEmpty) {
        this.allFieldsEmpty = true;
        // console.log('All fields are empty');
      } else {
        this.allFieldsEmpty = false;
        // console.log('All fields are not empty');
      }
    });

    this.listOfClients$ = this.benefitService.getClientsListByAccess().pipe(
      tap((res) => {
        this.clientList = res;
        const hasAccessToAddMember = res.filter((e) => e.olA_Access_Type !== UserAccessLevel.LEVEL_A && e.olA_Access_Type !== UserAccessLevel.LEVEL_E);
        this.showAddMember = hasAccessToAddMember.length !== 0;
      })
    );

    // Filter clients based on search input
    this.filteredClients = this.clientFilterControl.valueChanges.pipe(
      startWith(''), // Initially show all clients
      switchMap(searchTerm => this.filterClients(searchTerm)) // Filter clients when search term changes
    );

    this.loader.hideLoader();

    let accessLevel;
    this.subscription.push(this.sharedService.accessLevel$.subscribe((v) => {
      accessLevel = v
      if (accessLevel) {
        this.memberMenus = this.utilService.getEligibilityMenus(accessLevel);

        if (this.memberMenus.find((x) => x.key === EligibilityMenus.AddNewMember)) {
          const addMemberMenu = this.memberMenus.find(
            (x) => x.key === EligibilityMenus.AddNewMember
          );
          addMemberMenu.show = false;
          this.hasAccessToAddMember = true;
        }
      }
    }));

    // Update member details based on search criteria
    this.members$ = this.getMembers();

    // console.log('eligibilityMenus', this.memberMenus);

    this.subscription.push(this.eligibilityService.selectedClient3$
      .pipe(
        filter((client) => !!client),
        distinctUntilChanged()
      )
      .subscribe((client) => {
        // console.log('Client in subject member search:', client);

        this.searchMemberFormGroup.get('client').setValue(client.clientId);
        if (this.eligibilityService.searchCriteriaCache?.client !== this.searchMemberFormGroup.value.client) {
          this.searchClicked = false;
          this.onTabChange();
          this.onClientSelection(client);
        }
      }));
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  // Function to filter clients based on the search term
  private filterClients(searchTerm: string): Observable<Clients[]> {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return this.listOfClients$.pipe(
      map(clients =>
        clients.filter(client =>
          client.clientName.toLowerCase().includes(lowerCaseTerm) || client.clientId.includes(lowerCaseTerm)
        )
      )
    );
  }

  onChangeIdType(event: any) {
    this.searchMemberFormGroup.controls['idValue'].patchValue('');
    this.dataLoaded = false;
  }

  formatSSNString(ssn: string): string {
    return formatSSN(ssn);
  }

  onTabChange(event?: any) {
    // console.log('On tab change:', event);
    this.searchMemberFormGroup.controls['idType'].patchValue('');
    this.searchMemberFormGroup.controls['idValue'].patchValue('');
    this.searchMemberFormGroup.controls['firstName'].patchValue('');
    this.searchMemberFormGroup.controls['lastName'].patchValue('');
    this.dataLoaded = false;
  }

  get isLevelD(): boolean {
    return this.sharedService.isLevelD();
  }

  public getToolTip(): string {
    return 'Update Member';
  }

  public checkIfCoverageExpired(element): boolean {
    return new Date(element.coverage.endDate).valueOf() < new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf() && !element.isSoftDeleted
  }

  public checkIfCoverageIsStartingSoon(element): boolean {
    return (
      new Date(element.coverage.startDate).valueOf() > new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf() && !element.isSoftDeleted
    );
  }

  public checkIfCoverageSoftDeleted(element): boolean {
    return element.isSoftDeleted;
  }

  public isValidForm(): boolean {
    let isValid = false;
    if (this.eligibilityService.selectedIndex === 0) {
      isValid =
        this.searchMemberFormGroup.controls.client.valid &&
        this.searchMemberFormGroup.controls.idType.valid &&
        this.searchMemberFormGroup.controls.idValue.valid;
    } else {
      isValid =
        this.searchMemberFormGroup.controls.client.valid &&
        this.searchMemberFormGroup.controls.lastName.valid &&
        this.searchMemberFormGroup.controls.firstName.valid &&
        (this.searchMemberFormGroup.controls.firstName.value ||
          this.searchMemberFormGroup.controls.lastName.value);
    }
    return isValid;
  }

  private getMembers(): Observable<IMemberSearchResponse> {
    return defer(() => {
      return this.eligibilityService.searchMembers().pipe(
        map((res) => {
          if (res) {
            this.setDataSource(res);
            this.setUpPagination(res);
            // setTimeout(() => {
            //   this.dataSource.paginator = this.paginator;
            // }, 0);
            this.dataLoaded = true;
            // console.log(
            //   'Subscription:',
            //   !!this.eligibilityService.selectedClientSubject['_value']
            // );
          }
          return res;
        }),
        catchError((err) => {
          this.dataLoaded = false;
          return of({
            members: [],
            total_count: 0
          });
        })
      );
    });
  }

  private setUpPagination(res: IMemberSearchResponse) {
    this.eligibilityService.queryString.subscribe((query) => {
      const offset = getQueryParam(query, 'offset');
      const perSize = getQueryParam(query, 'limit');
      setTimeout(() => {
        if (offset && this.paginator) {
          this.paginator.pageSize = perSize ? parseInt(perSize) : 10;
          const perPage = this.paginator.pageSize ? this.paginator.pageSize : perSize ? parseInt(perSize) : 10;
          this.paginator.pageIndex = parseInt(offset) / perPage;
        }
      }, 100);
    });

    if (res.total_count) { this.totalLength = res.total_count; }
    if (this.hidePaginator) {
      this.hidePaginator = false;
    }
  }

  private setDataSource(res: IMemberSearchResponse): void {
    if (this.isGroupLevelAccess) {
      res.members.forEach(m => {
        const matchingGroup = this.groupLevelAccess.find(r => r.groupId?.toUpperCase() === m.coverage.groupNumber.toUpperCase());
        if (matchingGroup) {
          m.accessLevel = matchingGroup.benefitEligibilityAccessType;
        }
      });
    }
    if (this.searchMemberFormGroup.controls.firstName.value || this.searchMemberFormGroup.controls.lastName.value) {
      this.dataSource = new MatTableDataSource(res.members);
    } else {
      const softDeletedUsers = res.members.filter((m) => m.isSoftDeleted).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
      const nonSoftDeletedUsers = res.members.filter((m) => !m.isSoftDeleted).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
      const termedUsers = nonSoftDeletedUsers.filter((m) => new Date(m.coverage.endDate).valueOf() < new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
      const activeUsers = nonSoftDeletedUsers.filter((m) => new Date(m.coverage.endDate).valueOf() >= new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
      this.dataSource = new MatTableDataSource([...activeUsers, ...termedUsers, ...softDeletedUsers]);
    }
  }

  public searchMembers(perPage?: number, pageIndex?: number | string) {
    this.searchClicked = true;
    if (!perPage && !pageIndex) {
      this.totalLength = 0;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.paginator.pageSize = 10
      }
    }
    const searchQuery: MemberSearchRequest = {
      clientId: this.searchMemberFormGroup.controls.client.value,
    };
    // console.log('Search query:', searchQuery);

    if (this.eligibilityService.selectedIndex === 0) {
      const idType = this.searchMemberFormGroup.controls.idType.value;
      const idValue =
        this.searchMemberFormGroup.controls.idType.value === 'SSN'
          ? clearSsnFormat(this.searchMemberFormGroup.controls.idValue.value)
          : this.searchMemberFormGroup.controls.idValue.value.trim();

      switch (idType) {
        case 'Card/Alt ID':
          searchQuery.cardId = idValue;
          break;
        case 'SSN':
          searchQuery.SSN = idValue;
          break;
        // case 'EmpiRx Member ID':
        //   searchQuery.empirxMemberId = idValue;
        //   break;
        case 'Client Internal Member ID':
          searchQuery.clientInternalMemberId = idValue;
          break;
      }
    } else {
      searchQuery.firstName =
        this.searchMemberFormGroup.controls.firstName?.value?.trim();
      searchQuery.lastName = this.searchMemberFormGroup.controls.lastName?.value?.trim();
    }
    // console.log('Search query:', searchQuery);
    // this.eligibilityService.clientChosenMemberSearch.next(searchQuery.clientId);
    if (
      searchQuery.clientId === '19121' &&
      this.searchMemberFormGroup.controls.idType.value === 'Card/Alt ID'
    ) {
      searchQuery.cardId = trimIfUNE(searchQuery.cardId);
    }
    searchQuery.limit = perPage ? perPage : 10,
      searchQuery.offset = pageIndex ? pageIndex : '0',
      searchQuery.count = perPage ? 0 : 1;
    this.appInsightService.trackPageView(AppCenterAnalytics.GET_ONLINE_ELIGIBILITY_SEARCH_REQUEST, { data: searchQuery });
    this.eligibilityService.queryString.next(
      this.eligibilityService.getSearchQuery(searchQuery)
    );
  }

  public resetForm() {
    // console.log(this.searchMemberFormGroup.value);
    this.searchMemberFormGroup.controls['idType'].reset();
    this.searchMemberFormGroup.controls['idValue'].reset();
    this.searchMemberFormGroup.controls['firstName'].reset();
    this.searchMemberFormGroup.controls['lastName'].reset();
  }

  public addNewMember(): void {
    this.router.navigateByUrl('/management/online-eligibility/add-new-member');
  }
  public addNewDependent(member: MemberDetails): void {
    const queryParams = {
      clientId: member.clientId,
      cardId: member.cardId,
      patientId: member.patientId,
      firstName: member.firstName,
      groupNumber: member.coverage.groupNumber
    };
    this.router.navigate(['/management/online-eligibility/add-new-dependent'], {
      queryParams: queryParams,
    });
  }
  public updateMember(member: MemberDetails): void {
    // console.log('Member details:', member);
    const queryParams = {
      clientId: member.clientId,
      cardId: member.cardId,
      patientId: member.patientId,
      firstName: member.firstName,
      groupNumber: member.coverage.groupNumber
    };
    this.router.navigate(['/management/online-eligibility/update-member'], {
      queryParams: queryParams,
    });
  }
  public updateCoverage(): void {
    this.router.navigateByUrl('/management/online-eligibility/update-coverage');
  }

  public goToMenu(menu: EligibilityMenu, member: MemberDetails) {
    const queryParams = {
      clientId: member.clientId,
      cardId: member.cardId,
      patientId: member.patientId,
      firstName: member.firstName,
      groupNumber: member.coverage.groupNumber
    };
    if (menu.key === EligibilityMenus.UpdateCoverage) {
      queryParams['cardHolderRCGrpNum'] = this.dataSource.data.filter((m) => m.relationshipCode === this.cardHolderRC && m.cardId === member.cardId)[0]?.coverage?.groupNumber;
    }
    this.router.navigate([menu.route], { queryParams: queryParams });
  }

  public onPageChange(pageEvent: PageEvent) {
    const pageIndex = pageEvent.pageIndex ? pageEvent.pageIndex * pageEvent.pageSize : '0';
    this.searchMembers(pageEvent.pageSize, pageIndex);
  }

  public onClientSelection(client: any) {
    // console.log('on Client selection', client.value);
    // this.eligibilityService.selectedClientSubject.next(client);
    this.searchMemberFormGroup.controls['idType'].patchValue('');
    this.searchMemberFormGroup.controls['idValue'].patchValue('');
    this.searchMemberFormGroup.controls['firstName'].patchValue('');
    this.searchMemberFormGroup.controls['lastName'].patchValue('');
    this.dataLoaded = false;
    this.isDemoClient(client.clientId);
  }

  onManuallyClientSelect(client: Clients) {
    sessionStorage.setItem(AppConstants.LABELS.clientId, client.clientId);
    this.eligibilityService.setSelectedClient2(client);
    this.eligibilityService.setSelectedClient3(client);
    this.sharedService.updateAccessLevel(client.olA_Access_Type);
    this.eligibilityService.updateIsMigratedValue(client.migrationFlag && client.migrationFlag === 'Y');
    this.eligibilityService.updateIsGroupLevelAccessValue(client?.groupLevelFlag);
  }

  isDemoClient(clientId: string) {
    this.loader.showLoader();
    this.demoClientService.checkDemoClient(clientId).subscribe({
      next: (res) => {
        // console.log('Response on isDemoClient:', res);
        this.demoClientService.updateDemoClientFlag = res;
      },
      error: (err) => {
        this.loader.hideLoader();
      },
      complete: () => {
        this.loader.hideLoader();
      },
    });
  }

  formatPhoneNumber(phone: string): string {
    return formatPhone(phone);
  }

  public toggle() {
    this.allRowsExpanded = !this.allRowsExpanded;
    this.expandedElement = null;
  }

  public getCoverageTableDatasource(
    data: Coverage[]
  ): MatTableDataSource<Coverage> {
    // console.log('Data found:', data);
    return new MatTableDataSource(data);
  }

  public getCoverageTypesTableDatasource(
    data: MemberDetails
  ): MatTableDataSource<RxTypeInterface> {
    // console.log('Data found:', data);
    const coverageTypesData: RxTypeInterface[] = [];
    coverageTypesData.push(data.flexTag6);
    if (data.flexTag5.flexTagName) {
      coverageTypesData.push(data.flexTag5);
    }
    coverageTypesData.push(data.flexTag4);
    coverageTypesData.push(data.flexTag3);
    coverageTypesData.push(data.flexTag2);
    coverageTypesData.push(data.flexTag);
    return new MatTableDataSource(coverageTypesData.filter(flexTag => flexTag.flexTagName));
  }

  public getMenuOptions(member: MemberDetails): EligibilityMenu[] {
    let accessLevel;
    let memberMenus;
    this.subscription.push(this.sharedService.accessLevel$.subscribe((v) => {
      accessLevel = v
      if (accessLevel) {
        memberMenus = this.utilService.getEligibilityMenus(member.accessLevel || accessLevel);
      }
    }));
    const array = Object.assign([], memberMenus);
    this.checkIfHasMenuAccess(array, EligibilityMenus.AddNewMember);
    this.checkIfHasMenuAccess(array, EligibilityMenus.AddNewDependent);
    this.checkIfHasMenuAccess(array, EligibilityMenus.UpdateMember);

    if (member.relationshipCode !== this.cardHolderRC) {
      this.checkIfHasMenuAccess(array, EligibilityMenus.RequestIdCard);
      const actionsToBeRemoved = [
        EligibilityMenus.DrugPricing,
        EligibilityMenus.PharmacySearch,
        EligibilityMenus.PrintDigitalIdCard,
      ];
      this.checkIfCoverageHasExpired(member, array, actionsToBeRemoved);
    }
    if (member.relationshipCode === this.cardHolderRC) {
      const actionsToBeRemoved = [
        EligibilityMenus.DrugPricing,
        EligibilityMenus.PharmacySearch,
        EligibilityMenus.PrintDigitalIdCard,
        EligibilityMenus.RequestIdCard,
      ];
      this.checkIfCoverageHasExpired(member, array, actionsToBeRemoved);
    }
    if (searchClientId(this.clientIdsWithoutDigitalIdCard, member.clientId)) {
      const actionsToBeRemoved = [
        EligibilityMenus.PrintDigitalIdCard,
      ];
      this.removeMenus(actionsToBeRemoved, array);
    }

    if (searchClientId(this.clientIdsWithoutMailOrders, member.clientId)) {
      const actionsToBeRemoved = [
        EligibilityMenus.MailOrders,
        EligibilityMenus.Prescriptions
      ];
      this.removeMenus(actionsToBeRemoved, array);
    }

    if (new Date(member.coverage.startDate).valueOf() > new Date().valueOf()) {
      const actionsToBeRemoved = [
        EligibilityMenus.DrugPricing,
      ];
      this.removeMenus(actionsToBeRemoved, array);
    }

    if (member.isSoftDeleted) {
      const actionsToBeRemoved = [
        EligibilityMenus.AddNewDependent,
      ];
      this.removeMenus(actionsToBeRemoved, array);
    }
    this.checkUsersPharmacySpecificAccess(member, array);
    return array;
  }

  private checkUsersPharmacySpecificAccess(member: MemberDetails, array: EligibilityMenu[]): void {
    this.eligibilityService.getClientPhramaciesConfig().pipe(take(1)).subscribe((res) => {
      const clientPharmaciesConfig: IClientPharmacyConfig[] = res?.filter((p) => p.clientId === member.clientId);
      if (clientPharmaciesConfig?.length) {
        if (clientPharmaciesConfig[0].integrationType === 'SSO') {
          const actionsToBeRemoved = [
            EligibilityMenus.Prescriptions,
            EligibilityMenus.MailOrders
          ];
          this.removeMenus(actionsToBeRemoved, array);
          const pMartProfileStatusColumnIndex = this.displayedColumns.findIndex(col => col === 'PMartProfileStatus');
          if (pMartProfileStatusColumnIndex !== -1) {
            this.displayedColumns.splice(pMartProfileStatusColumnIndex, 1);
          }
          return;
        }
        this.displayedColumns = [...AppConstants.DISPLAYED_MEMBER_SEARCH_COLUMNS];
      }
    })
  }

  private checkIfCoverageHasExpired(
    member: MemberDetails,
    array: any[] & EligibilityMenu[],
    actionsToBeRemoved: string[]
  ): void {
    if (new Date(member.coverage.endDate).valueOf() < new Date().valueOf()) {
      this.removeMenus(actionsToBeRemoved, array);
    }
  }

  private removeMenus(actionsToBeRemoved: string[], array: EligibilityMenu[]): void {
    actionsToBeRemoved.forEach((i) => {
      const index = array.findIndex((x) => x.key === i);
      if (index !== -1) {
        array.splice(index, 1);
      }
    });
  }

  private checkIfHasMenuAccess(array: any[] & EligibilityMenu[], menu: string): void {
    const index = array.findIndex((x) => x.key === menu);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  // public showAddNewMemberButton(): boolean {
  //   return !!this.memberMenus.find(
  //     (x) => x.key === EligibilityMenus.AddNewDependent
  //   );
  // }

  public onSelectionChange(event): void {
    const selectedClient = this.clientList.filter((c) => c.clientId === event.value)[0];
    this.onManuallyClientSelect(selectedClient);
  }

  public checkMenuAccess(
    member: MemberDetails,
    menuName: 'UpdateMember' | 'AddNewDependent'
  ): boolean {
    let accessLevel;
    let memberMenus;
    this.subscription.push(this.sharedService.accessLevel$.subscribe((v) => {
      accessLevel = v
      if (accessLevel) {
        memberMenus = this.utilService.getEligibilityMenus(member.accessLevel || accessLevel);
      }
    }));
    const array = Object.assign([], memberMenus);
    const menu = array.find((x) => x.key === menuName);

    if (menuName === 'AddNewDependent') {
      return !!menu && member.relationshipCode === this.cardHolderRC && !member.isSoftDeleted;
    } else {
      return !!menu;
    }
  }

  public copyFirstNameLastName(element: MemberDetails): string {
    return `${element.firstName} ${element.lastName}`
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.forEach((s) => s.unsubscribe());
    this.eligibilityService.searchCriteriaCache =
      this.searchMemberFormGroup.value;
    this.eligibilityService.searchCriteriaCache.total_count = this.totalLength;
  }
}
